/**
 * Phase layout algorithm for the Needs Map
 * Groups nodes by phase and arranges phases horizontally
 * Now with support for phase nodes and phase edges
 */

import { Node, Edge } from '@xyflow/react';
import { TopicNode } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';
import { 
  Phase, 
  PhaseNodeData, 
  PhaseEdgeData,
  createPhaseNodeId, 
  createPhaseEdgeId,
  generatePhaseFlowEdges
} from '@/types/phaseTypes';

/**
 * Phase flow connections
 * Defines the natural flow between phases
 * This can be customized based on the desired phase progression
 */
const DEFAULT_PHASE_FLOW: Array<[string, string]> = [
  ["fact_action", "fact_detail"],
  ["fact_detail", "thought_feeling"],
  ["thought_feeling", "value"],
  ["value", "career_view"],
  ["career_view", "company"]
];

/**
 * Apply phase layout to nodes with phase nodes
 * Groups nodes by their phase and arranges phases from left to right
 */
export function applyPhaseLayout(
  nodes: Node[],
  edges: Edge[],
  phases: Phase[],
  width: number = 1000,
  height: number = 600,
  defaultNodeWidth: number = 250,
  defaultNodeHeight: number = 150,
  horizontalGap: number = 180, // Increased horizontal gap for phase layout
  verticalGap: number = 60,     // Gap between nodes
  includePhaseNodes: boolean = true // Whether to include phase nodes
): Node[] {
  if (nodes.length === 0) return [];

  // Group nodes by phase
  const nodesByPhase = new Map<string, Node[]>();
  
  // Initialize with empty arrays for all phases
  phases.forEach(phase => {
    nodesByPhase.set(phase.id, []);
  });
  
  // Group nodes
  nodes.forEach(node => {
    const phaseId = node.data?.phaseId as string | undefined;
    if (phaseId && nodesByPhase.has(phaseId)) {
      const phaseNodes = nodesByPhase.get(phaseId) || [];
      phaseNodes.push(node);
      nodesByPhase.set(phaseId, phaseNodes);
    }
  });
  
  const positionedNodes: Node[] = [];
  
  // If including phase nodes, create phase nodes and position them
  if (includePhaseNodes) {
    const phaseWidth = 280; // Width of phase nodes
    const phaseHeight = 120; // Min height of phase nodes
    const phaseMargin = 40; // Top margin for phase nodes
    const phaseTopY = phaseMargin; // Y position for phase nodes
    
    // Create and position phase nodes first
    phases.forEach((phase, phaseIndex) => {
      const phaseX = horizontalGap + (phaseWidth + horizontalGap) * phaseIndex;
      
      // Count nodes in this phase for the badge
      const childCount = (nodesByPhase.get(phase.id) || []).length;
      
      // Create phase node
      const phaseNode: Node = {
        id: createPhaseNodeId(phase.id),
        type: 'phaseNode', // Custom node type for phases
        position: { x: phaseX, y: phaseTopY },
        data: {
          id: phase.id,
          name: phase.name,
          description: phase.description,
          color: phase.color,
          childCount: childCount,
          width: phaseWidth,
          height: phaseHeight
        }
      };
      
      positionedNodes.push(phaseNode);
    });
  }
  
  // Position the regular topic nodes by phase
  phases.forEach((phase, phaseIndex) => {
    const phaseNodes = nodesByPhase.get(phase.id) || [];
    
    // Calculate phase column x position (centered under phase node if present)
    let phaseColumnX;
    if (includePhaseNodes) {
      const phaseWidth = 280;
      phaseColumnX = horizontalGap + (phaseWidth + horizontalGap) * phaseIndex + (phaseWidth - defaultNodeWidth) / 2;
    } else {
      // If no phase nodes, use simpler spacing
      phaseColumnX = horizontalGap + (defaultNodeWidth + horizontalGap) * phaseIndex;
    }
    
    // Sort nodes by importance (descending)
    phaseNodes.sort((a, b) => {
      const importanceA = (a.data?.importance as number) || 0;
      const importanceB = (b.data?.importance as number) || 0;
      return importanceB - importanceA;
    });
    
    // Calculate starting Y position - leave space for phase node if included
    const startY = includePhaseNodes ? 180 : 50;
    
    // Position nodes in this phase
    phaseNodes.forEach((node, nodeIndex) => {
      // Use provided width/height or defaults
      const nodeWidth = defaultNodeWidth;
      const nodeHeight = defaultNodeHeight;
      
      // Calculate y position with vertical gap
      const y = startY + nodeIndex * (nodeHeight + verticalGap);
      
      positionedNodes.push({
        ...node,
        position: { x: phaseColumnX, y },
        data: {
          ...node.data,
          width: nodeWidth,
          height: nodeHeight
        }
      });
    });
  });
  
  return positionedNodes;
}

/**
 * Apply phase layout to topic nodes with phase nodes
 */
export function applyPhaseLayoutToTopicNodes(
  nodes: TopicNode[],
  edges: TopicEdge[],
  phases: Phase[] = [],
  width: number = 1000,
  height: number = 600,
  defaultNodeWidth: number = 250,
  defaultNodeHeight: number = 150,
  includePhaseNodes: boolean = true // Whether to include phase nodes
): TopicNode[] {
  // First convert to ReactFlow nodes for positioning
  const rfNodes = nodes.map(node => ({
    id: node.id,
    position: node.position || { x: 0, y: 0 },
    data: {
      ...node,
      phaseId: node.phaseId,
      width: defaultNodeWidth,
      height: defaultNodeHeight
    }
  }));
  
  // Apply layout algorithm
  const positionedRfNodes = applyPhaseLayout(
    rfNodes, 
    [], // Edges not needed for positioning
    phases,
    width,
    height,
    defaultNodeWidth,
    defaultNodeHeight,
    180, // Horizontal gap
    60,  // Vertical gap
    includePhaseNodes
  );
  
  // Find only the topic nodes (excluding any added phase nodes)
  const positionedTopicRfNodes = positionedRfNodes.filter(
    node => nodes.some(n => n.id === node.id)
  );
  
  // Map positions back to topic nodes
  return nodes.map(node => {
    const positionedNode = positionedTopicRfNodes.find(n => n.id === node.id);
    
    return {
      ...node,
      position: positionedNode?.position || node.position,
      width: defaultNodeWidth,
      height: defaultNodeHeight
    };
  });
}

/**
 * Generate all nodes and edges for phase layout including phase nodes
 */
export function generatePhaseLayoutElements(
  topicNodes: TopicNode[],
  topicEdges: TopicEdge[],
  phases: Phase[],
  width: number = 1000,
  height: number = 600,
  defaultNodeWidth: number = 250,
  defaultNodeHeight: number = 150
): { nodes: Node[], edges: Edge[] } {
  // Position the topic nodes first using phase layout
  const positionedTopicNodes = applyPhaseLayoutToTopicNodes(
    topicNodes,
    topicEdges,
    phases,
    width,
    height,
    defaultNodeWidth,
    defaultNodeHeight,
    true // Include phase nodes
  );
  
  // Convert to ReactFlow nodes
  const rfTopicNodes: Node[] = positionedTopicNodes.map(node => ({
    id: node.id,
    type: 'topicNode',
    position: node.position || { x: 0, y: 0 },
    data: {
      ...node,
      width: defaultNodeWidth,
      height: defaultNodeHeight
    }
  }));
  
  // Count nodes by phase
  const nodeCountByPhase: Record<string, number> = {};
  topicNodes.forEach(node => {
    if (node.phaseId) {
      nodeCountByPhase[node.phaseId] = (nodeCountByPhase[node.phaseId] || 0) + 1;
    }
  });
  
  // Create phase nodes
  const phaseWidth = 280;
  const phaseHeight = 120;
  const phaseMargin = 40;
  const horizontalGap = 180;
  
  const phaseNodes: Node[] = phases.map((phase, index) => {
    const phaseX = horizontalGap + (phaseWidth + horizontalGap) * index;
    
    return {
      id: createPhaseNodeId(phase.id),
      type: 'phaseNode',
      position: { x: phaseX, y: phaseMargin },
      data: {
        id: phase.id,
        name: phase.name,
        description: phase.description,
        color: phase.color,
        childCount: nodeCountByPhase[phase.id] || 0,
        width: phaseWidth,
        height: phaseHeight
      }
    };
  });
  
  // Create phase edges using the defined flow
  const phaseFlowData = generatePhaseFlowEdges(phases, DEFAULT_PHASE_FLOW);
  
  // Convert phase edges to ReactFlow edges
  const phaseEdges: Edge[] = phaseFlowData.map(flowEdge => ({
    id: flowEdge.id,
    source: flowEdge.source,
    target: flowEdge.target,
    type: 'phaseEdge',
    // Cast to any to avoid incompatible type errors
    data: flowEdge.data as any
  }));
  
  // Convert topic edges to ReactFlow edges
  const rfTopicEdges: Edge[] = topicEdges.map(edge => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    type: 'customEdge',
    data: { ...edge }
  }));
  
  return {
    nodes: [...phaseNodes, ...rfTopicNodes],
    edges: [...phaseEdges, ...rfTopicEdges]
  };
}